import type { PriceImportRow, PriceImportRowStatus } from 'apis/price-imports';

/**
 * The three piles an operator actually works through. Deliberately fewer than
 * the BE's nine row statuses: the reviewer's question is only ever "is this
 * ready, does it need me, or is it parked".
 */
export type RowBucket = 'ready' | 'decide' | 'hold';

const BUCKET_BY_STATUS: Record<PriceImportRowStatus, RowBucket> = {
  matched: 'ready',
  approved: 'ready',
  committed: 'ready',
  needs_review: 'decide',
  proposed_new: 'decide',
  // An error still needs a person, so it belongs with the work, not parked.
  error: 'decide',
  commit_failed: 'decide',
  skipped: 'hold',
  rejected: 'hold',
};

export function bucketOf(status: PriceImportRowStatus): RowBucket {
  return BUCKET_BY_STATUS[status] ?? 'decide';
}

export const BUCKET_LABEL: Record<RowBucket, string> = {
  ready: 'Ready',
  decide: 'Needs decision',
  hold: 'On hold',
};

export const BUCKET_COLOR_SCHEME: Record<RowBucket, string> = {
  ready: 'green',
  decide: 'orange',
  hold: 'gray',
};

/** Bucket pill counts. Must come from the run summary, never a loaded page. */
export function countBuckets(rows: Pick<PriceImportRow, 'status'>[]): Record<RowBucket, number> {
  return rows.reduce(
    (counts, row) => {
      counts[bucketOf(row.status)] += 1;
      return counts;
    },
    { ready: 0, decide: 0, hold: 0 } as Record<RowBucket, number>
  );
}

/** Does anything block this row from committing? */
export function hasBlockingIssue(row: Pick<PriceImportRow, 'issues'>): boolean {
  return (row.issues ?? []).some((issue) => issue.severity === 'error');
}
